/**
 * Roguelite Checkpoint Detection — detects when the human player
 * reaches a checkpoint platform and triggers a card draw.
 */

import type { GameState } from '../types';
import type { GameEffect } from '../game/effects';
import { transitionPhase } from '../game/effects';
import { ROGUELITE } from '../constants';
import { drawWeaponCards, drawModifierCards } from './card-draw';

/** Check if the human player has reached an unreached checkpoint. Call during aiming phase. */
export function checkForCheckpoint(state: GameState, fx: GameEffect[]): boolean {
  if (state.modeState.mode !== 'roguelite') return false;

  const human = state.players[0];
  if (!human || !human.alive || !human.grounded) return false;

  const ms = state.modeState;
  const checkpoints = ms.rogueliteCheckpointYs;

  for (let i = 0; i < checkpoints.length; i++) {
    const cpY = checkpoints[i];
    // Already reached this checkpoint?
    if (ms.rogueliteCheckpointsReached.includes(cpY)) continue;

    // Check proximity (human Y is near checkpoint Y)
    if (Math.abs(human.y - cpY) < ROGUELITE.CHECKPOINT_PROXIMITY) {
      // Reached! Mark it
      ms.rogueliteCheckpointsReached.push(cpY);

      // Advance zone
      ms.rogueliteZone = i + 2; // checkpoint i marks entry to zone i+2

      // Determine draw type for this checkpoint
      const drawType = ROGUELITE.ZONE_DRAW_TYPE[i] as 'weapon1' | 'weapon2' | 'modifier';
      ms.rogueliteCardDrawType = drawType;

      // Generate card options
      if (drawType === 'weapon1' || drawType === 'weapon2') {
        ms.rogueliteCardDrawOptions = drawWeaponCards(state, i + 1);
      } else {
        ms.rogueliteCardDrawOptions = drawModifierCards(state, i + 1);
      }

      ms.rogueliteCardDrawCursor = 0;
      ms.rogueliteVoidPauseTimer = ROGUELITE.VOID_PAUSE_DURATION;

      // Transition to card draw phase
      transitionPhase(state, 'roguelite_card_draw', fx);
      fx.push({ type: 'roguelite_card_draw_show' });
      fx.push({ type: 'roguelite_zone_enter', zone: ms.rogueliteZone });

      return true;
    }
  }

  return false;
}
